import React, { useState, useRef, useEffect } from "react";
import { noraSurfaceEnabled } from "../../utils/noraSurface";
import * as Sentry from "@sentry/react";
import { sendNoraMessage } from "../../api/noraService";

/**
 * NoraChatPanel — GMA-1520
 *
 * "Ask Nora about this client" panel for the web client profile. A caregiver
 * types a question about the client; the backend (GMA-1519, POST
 * /api/v3/ai/chat) validates the caregiver's access to the client and grounds
 * Nora's reply in that client's Pontosense / Silver Shield data.
 *
 * PRIVACY: no Client PII/PHI is logged. Breadcrumbs carry the clientId only —
 * never message content or client names.
 */
const styles = {
  // This panel supplies its own light surface + dark text (like the
  // Pontosense/Watch panes) so it stays readable regardless of the page
  // background — the profile page was black when this shipped (GMA-1520)
  // and is bg-page-light since #639.
  panel: {
    border: "1px solid #e2e8f0",
    borderRadius: 8,
    padding: 16,
    marginTop: 16,
    backgroundColor: "#ffffff",
    color: "#0f172a",
  },
  heading: { color: "#0f172a", marginTop: 0 },
  hint: { color: "#64748b", fontSize: 13, marginTop: 0 },
  messages: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    maxHeight: 320,
    overflowY: "auto",
    padding: "8px 0",
  },
  msgUser: { alignSelf: "flex-end", background: "#dbeafe", borderRadius: 8, padding: "6px 10px", maxWidth: "80%" },
  msgNora: { alignSelf: "flex-start", background: "#f1f5f9", borderRadius: 8, padding: "6px 10px", maxWidth: "80%" },
  role: { display: "block", fontSize: 11, fontWeight: 600, color: "#475569" },
  content: { whiteSpace: "pre-wrap" },
  error: { color: "#b91c1c", fontSize: 13 },
  composer: { display: "flex", gap: 8, marginTop: 8 },
  textarea: { flex: 1, resize: "vertical", padding: 8, borderRadius: 6, border: "1px solid #cbd5e1" },
  button: { padding: "8px 16px", borderRadius: 6 },
};

const NoraChatPanel = ({ clientId, clientName, caregiverName }) => {
  // GMA-1781: Nora renders only on the Command Center domain. Gated inside
  // the component (not at each call site) so no current or future mount
  // point can leak it onto the legacy frontends.
  if (!noraSurfaceEnabled()) return null;
  return (
    <NoraChatPanelInner
      clientId={clientId}
      clientName={clientName}
      caregiverName={caregiverName}
    />
  );
};

const NoraChatPanelInner = ({ clientId, clientName, caregiverName }) => {
  const [messages, setMessages] = useState([]); // [{ role, content }]
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const listEndRef = useRef(null);

  useEffect(() => {
    // Optional chain on the method too: jsdom (tests) has no scrollIntoView.
    listEndRef.current?.scrollIntoView?.({ behavior: "smooth" });
  }, [messages, loading]);

  // GMA-1573: a caregiver can switch client profiles while this panel stays
  // mounted (ClientProfilePage updates selectedClient in ClientContext; the
  // route is not remounted). Reset the conversation whenever the client changes
  // so client A's turns are never shown under — or sent with — client B.
  useEffect(() => {
    setMessages([]);
    setInput("");
    setError(null);
  }, [clientId]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || loading) return;

    // History is the conversation BEFORE this turn, in the backend's format.
    const history = messages.map((m) => ({ role: m.role, content: m.content }));
    setMessages((prev) => [...prev, { role: "user", content: text }]);
    setInput("");
    setError(null);
    setLoading(true);

    try {
      Sentry.addBreadcrumb({
        category: "nora.web.chat",
        message: "sent",
        level: "info",
        data: { clientId },
      });
    } catch (_ignored) {
      // Sentry not initialised (tests, local dev) — swallow so chat isn't broken.
    }

    try {
      const { reply } = await sendNoraMessage({
        clientId,
        message: text,
        history,
        clientName,
        caregiverName,
      });
      setMessages((prev) => [...prev, { role: "assistant", content: reply || "" }]);
    } catch (err) {
      try {
        Sentry.addBreadcrumb({
          category: "nora.web.chat",
          message: "error",
          level: "error",
          data: { clientId },
        });
      } catch (_ignored) {
        // Sentry not initialised — swallow.
      }
      setError(err?.message || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <section
      className="nora-chat-panel"
      style={styles.panel}
      aria-label="Ask Nora about this client"
    >
      <h3 style={styles.heading}>
        Ask Nora{clientName ? ` about ${clientName}` : ""}
      </h3>
      <p className="nora-chat-panel__hint" style={styles.hint}>
        Ask about sleep, falls, sensor status, or anything in{" "}
        {clientName || "the client"}&apos;s record. Nora answers from available
        data only.
      </p>

      <div
        className="nora-chat-panel__messages"
        style={styles.messages}
        role="log"
        aria-live="polite"
      >
        {messages.length === 0 && !loading && (
          <p className="nora-chat-panel__empty" style={styles.hint}>
            No messages yet. Ask Nora a question below.
          </p>
        )}
        {messages.map((m, i) => (
          <div
            key={i}
            className={`nora-chat-panel__message nora-chat-panel__message--${m.role}`}
            style={m.role === "user" ? styles.msgUser : styles.msgNora}
          >
            <span className="nora-chat-panel__role" style={styles.role}>
              {m.role === "user" ? "You" : "Nora"}
            </span>
            <span className="nora-chat-panel__content" style={styles.content}>
              {m.content}
            </span>
          </div>
        ))}
        {loading && (
          <p className="nora-chat-panel__loading" style={styles.hint}>
            Nora is thinking&hellip;
          </p>
        )}
        <div ref={listEndRef} />
      </div>

      {error && (
        <p className="nora-chat-panel__error" style={styles.error} role="alert">
          {error}
        </p>
      )}

      <div className="nora-chat-panel__composer" style={styles.composer}>
        <textarea
          aria-label="Message to Nora"
          style={styles.textarea}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={`Ask Nora about ${clientName || "this client"}…`}
          rows={2}
          disabled={loading}
        />
        <button
          type="button"
          style={styles.button}
          onClick={handleSend}
          disabled={loading || !input.trim()}
        >
          Send
        </button>
      </div>
    </section>
  );
};

export default NoraChatPanel;